import { environment } from "@app/relay/relay-environment";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { commitMutation } from "react-relay";

import {
  scheduleDeleteMutation,
  scheduleSaveMutation,
} from "./queue-fragment";
import type { queueFragment_ScheduleSaveMutation } from "@app/queries/queueFragment_ScheduleSaveMutation.graphql";
import type { queueFragment_ScheduleDeleteMutation } from "@app/queries/queueFragment_ScheduleDeleteMutation.graphql";

// Work that runs without anyone clicking.
//
// Deliberately plain: a kind, an interval, and a switch. The alternative was a
// cron field, which would let somebody express "the third Tuesday" and would
// also let them express nothing at all by getting a character wrong — with no
// feedback until the run silently failed to happen.
export type Schedule = {
  readonly scheduleId: number;
  readonly kind: string;
  readonly label: string;
  readonly cadence: string;
  readonly everyMinutes: number;
  readonly nextRunAt: string;
  readonly enabled: boolean;
  readonly due: boolean;
  readonly lastRunAt: string | null | undefined;
  readonly lastJobId: number | null | undefined;
  readonly createdBy: string | null | undefined;
};

export type StartableKind = {
  readonly kind: string;
  readonly label: string;
};

// The intervals worth offering. Anything finer than the clock's own tick could
// never be honoured, and anything past a week is better expressed as a reminder
// to a person than as a standing job.
const INTERVALS = [
  { minutes: 60, label: "Hourly" },
  { minutes: 360, label: "Every 6 hours" },
  { minutes: 720, label: "Every 12 hours" },
  { minutes: 1440, label: "Daily" },
  { minutes: 10080, label: "Weekly" },
] as const;

function whenLabel(iso: string): string {
  const at = new Date(iso);
  const mins = Math.round((at.getTime() - Date.now()) / 60000);
  if (mins <= 0) return "due now";
  if (mins < 60) return `in ${mins} min`;
  if (mins < 1440) return `in ${Math.round(mins / 60)}h`;
  return `in ${Math.round(mins / 1440)}d`;
}

export default function QueueSchedules({
  schedules,
  startable,
  busy,
  onChanged,
  onNotice,
}: {
  schedules: ReadonlyArray<Schedule>;
  startable: ReadonlyArray<StartableKind>;
  busy: boolean;
  onChanged: () => void;
  onNotice: (message: string | null) => void;
}) {
  const [kind, setKind] = useState<string>(startable[0]?.kind ?? "");
  const [everyMinutes, setEveryMinutes] = useState<number>(1440);
  const [saving, setSaving] = useState(false);

  const save = (variables: {
    scheduleId?: number;
    kind?: string;
    everyMinutes?: number;
    enabled?: boolean;
  }) => {
    setSaving(true);
    onNotice(null);
    commitMutation<queueFragment_ScheduleSaveMutation>(environment, {
      mutation: scheduleSaveMutation,
      variables: {
        scheduleId: variables.scheduleId ?? null,
        kind: variables.kind ?? null,
        everyMinutes: variables.everyMinutes ?? null,
        enabled: variables.enabled ?? null,
      },
      onCompleted: (response, errors) => {
        setSaving(false);
        const failure =
          (errors ?? response.jobScheduleSave?.errors)?.[0]?.message ?? null;
        if (failure) onNotice(failure);
        onChanged();
      },
      onError: () => {
        setSaving(false);
        onNotice("Could not save that schedule. Please try again.");
      },
    });
  };

  const remove = (scheduleId: number) => {
    setSaving(true);
    commitMutation<queueFragment_ScheduleDeleteMutation>(environment, {
      mutation: scheduleDeleteMutation,
      variables: { scheduleId },
      onCompleted: () => {
        setSaving(false);
        onChanged();
      },
      onError: () => {
        setSaving(false);
        onNotice("Could not remove that schedule.");
      },
    });
  };

  const disabled = busy || saving;

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold text-gray-700">Scheduled</h2>
      <p className="max-w-3xl text-sm text-gray-500">
        Work that runs on its own. Every run lands on the queue exactly like one
        somebody asked for — same progress, same history — and is marked as
        having come from a schedule.
      </p>

      {schedules.length === 0 ? (
        <div className="rounded border border-dashed border-gray-300 p-4 text-sm text-gray-500">
          Nothing is scheduled on this account.
        </div>
      ) : (
        <ul className="divide-y rounded border border-gray-200 bg-white">
          {schedules.map((schedule) => (
            <li
              key={schedule.scheduleId}
              className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm"
            >
              <span className="font-medium text-gray-800">
                {schedule.label}
              </span>
              <span className="text-gray-500">{schedule.cadence}</span>
              <span
                className={
                  schedule.enabled ? "text-gray-400" : "text-gray-300 line-through"
                }
              >
                next {whenLabel(schedule.nextRunAt)}
              </span>
              {schedule.lastJobId && (
                <Link
                  to="/queue/$jobId"
                  params={{ jobId: String(schedule.lastJobId) }}
                  className="text-xs text-blue-600 hover:underline"
                >
                  last run #{schedule.lastJobId}
                </Link>
              )}
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() =>
                    save({
                      scheduleId: schedule.scheduleId,
                      enabled: !schedule.enabled,
                    })
                  }
                  className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                >
                  {schedule.enabled ? "Pause" : "Resume"}
                </button>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => remove(schedule.scheduleId)}
                  className="rounded border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50 disabled:opacity-40"
                >
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {startable.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="rounded border border-gray-200 px-2 py-1.5 text-sm"
          >
            {startable.map((option) => (
              <option key={option.kind} value={option.kind}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            value={everyMinutes}
            onChange={(e) => setEveryMinutes(Number(e.target.value))}
            className="rounded border border-gray-200 px-2 py-1.5 text-sm"
          >
            {INTERVALS.map((interval) => (
              <option key={interval.minutes} value={interval.minutes}>
                {interval.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={disabled || !kind}
            onClick={() => save({ kind, everyMinutes })}
            className="rounded border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            Schedule it
          </button>
        </div>
      )}
    </div>
  );
}
